import { createAction, handleActions } from 'redux-actions';
import { take, takeLatest } from 'redux-saga/effects';
import { call, put, delay } from 'redux-saga/effects';
import * as quizAPI from '../lib/api/quiz';
import * as analysisAPI from '../lib/api/analysis';
import * as shampooAPI from '../lib/api/shampoo';
import * as scalpAPI from '../lib/api/scalp';
import produce from 'immer';
import { createRequestActionTypes } from '../lib/createRequestSaga';
import { CHANGE_USER_QUIZ } from './user';
import { makeKit, stateGoodBadCategory } from '../lib/analysisHelper';
import { GET_TOP_RANKED_SHAMPOO_LIST_SUCCESS } from './shampoo';
import { GET_SCALP_TYPE_SUCCESS } from './scalp';

//----------------------------------------------------------------------------
// Action Type 정의
const UPDATE_QUIZ = 'quiz/UPDATE_QUIZ';
const RESET_QUIZ = 'quiz/RESET_QUIZ';
const INIT_QUIZ = 'quiz/INIT_QUIZ';
const CHANGE_QUIZ_SUBMIT_STATE = 'quiz/CHANGE_QUIZ_SUBMIT_STATE';
const CHANGE_USER_QUIZ_STATUS = 'quiz/CHANGE_USER_QUIZ_STATUS';
const UPDATE_PICKED_SHAMPOO = 'quiz/UPDATE_PICKED_SHAMPOO';
const UPDATE_KIT_HEADER = 'quiz/UPDATE_SELECT_KIT_HEADER';

// * quiz 마지막 페이지에서 제출하기 클릭 하였을 경우 페이지 분기를 위한 Action Type
const SET_LOCATION_FOR_QUIZ = 'quiz/SET_LOCATION_FOR_QUIZ';

const [POST_QUIZ, POST_QUIZ_SUCCESS, POST_QUIZ_FAILURE] =
  createRequestActionTypes('quiz/POST_QUIZ');
//======================= For KB START =======================
const [
  RECENT_QUIZ_RESULT_KB,
  RECENT_QUIZ_RESULT_KB_SUCCESS,
  RECENT_QUIZ_RESULT_KB_FAILURE,
] = createRequestActionTypes('quiz/RECENT_QUIZ_RESULT_KB');
const [
  RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB,
  RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_SUCCESS,
  RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_FAILURE,
] = createRequestActionTypes('quiz/RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB');

//======================= For KB END =======================
//----------------------------------------------------------------------------
// Action 생성
export const updateQuiz = createAction(UPDATE_QUIZ, ({ id, quiz, option }) => ({
  id,
  quiz,
  option,
}));

// * quiz 마지막 페이지에서 제출하기 클릭 하였을 경우 페이지 분기를 위한 Action
export const setLocationForQuiz = createAction(
  SET_LOCATION_FOR_QUIZ,
  (name) => name,
);

export const updatePicked = createAction(
  UPDATE_PICKED_SHAMPOO,
  (index, picked) => ({
    index,
    picked,
  }),
);
export const updateKitHeader = createAction(
  UPDATE_KIT_HEADER,
  (index, selectedKitProduct) => ({
    index,
    selectedKitProduct,
  }),
);

export const postQuizzes = createAction(
  POST_QUIZ,
  ({ quizzes, quizState, user_id, diagnosis_items, isBeconShampoo }) => ({
    quizzes,
    quizState,
    user_id,
    ...diagnosis_items,
    isBeconShampoo,
  }),
);
export const changeQuizSubmitState = createAction(CHANGE_QUIZ_SUBMIT_STATE);

export const resetQuiz = createAction(RESET_QUIZ, ({ id, quiz }) => ({
  id,
  quiz,
}));

export const initQuiz = createAction(INIT_QUIZ);

export const changeUserStatusForBeconShampoo = createAction(
  CHANGE_USER_QUIZ_STATUS,
  ({ isBeconShampoo }) => ({
    isBeconShampoo,
  }),
);

/** 퀴즈 최근 결과보기 -> 퀴즈이력   ======== For KB */
export const recentQuizResultKb = createAction(
  RECENT_QUIZ_RESULT_KB,
  ({ quizzes, quizState, user_id, activeUser }) => ({
    quizzes,
    quizState,
    user_id,
    activeUser,
  }),
);

/** 퀴즈 최근 결과보기 -> 퀴즈이력 + 두피진단  ======== For KB */
export const recentQuizResultWithDiagnosisKb = createAction(
  RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB,
  ({ quizzes, quizState, user_id, diagnosis_items, activeUser }) => ({
    quizzes,
    quizState,
    user_id,
    ...diagnosis_items,
    activeUser,
  }),
);

/** saga 퀴즈 최근 결과보기 -> (퀴즈이력 )  ======== For KB  */
function* recentQuizResultKbSaga({ payload }) {
  const { activeUser, quizState } = payload;
  try {
    // * 위드비컨 샴푸 조회
    const {
      data: { products },
    } = yield call(analysisAPI.getOwnProducts);

    const kit = makeKit({
      scalp_type: activeUser.scalp_type,
      scalp_type_2: activeUser.scalp_type_2 ? activeUser.scalp_type_2 : null,
      hair_preference: activeUser.preference.hair_1,
      treatment_product: activeUser.lifestyle.treatment_product,
      products,
      quizzes: quizState,
      product_variation: 'main',
    });

    const issues = stateGoodBadCategory(
      activeUser.scalp_type,
      activeUser,
      true,
    );

    yield put({
      type: RECENT_QUIZ_RESULT_KB_SUCCESS,
      data: { profile: activeUser, kit, issues },
    });
  } catch (error) {
    yield put({
      type: RECENT_QUIZ_RESULT_KB_FAILURE,
      error,
    });
  }
}

/** saga 퀴즈 최근 결과보기 -> (퀴즈이력 + 두피진단)  ======== For KB  */
function* recentQuizResultWithDiangosisKbSaga({ payload }) {
  const { quizState, activeUser } = payload;
  try {
    // * 위드비컨 샴푸 조회
    const {
      data: { products },
    } = yield call(analysisAPI.getOwnProducts);

    //* 두피 타입 조회
    const { data } = yield call(scalpAPI.getScalpType, payload);
    const { adjust_scalps } = data;
    const { scalp_types } = adjust_scalps;

    const kit = makeKit({
      scalp_type: scalp_types.primary,
      scalp_type_2: scalp_types?.secondary ? scalp_types.secondary : null,
      hair_preference: activeUser.preference.hair_1,
      treatment_product: activeUser.lifestyle.treatment_product,
      products,
      quizzes: quizState,
      product_variation: 'main',
    });

    const issues = stateGoodBadCategory(
      scalp_types.primary,
      adjust_scalps?.cares,
      false,
    );

    // scalp reducer 참고
    yield put({
      type: GET_SCALP_TYPE_SUCCESS,
      payload: data,
    });
    yield put({
      type: RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_SUCCESS,
      data: { profile: activeUser, kit, issues },
    });
  } catch (error) {
    yield put({
      type: RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_FAILURE,
      error,
    });
  }
}

//----------------------------------------------------------------------------
//사용자 문진 등록 saga
function* sumbmitQuizSaga(action) {
  try {
    // ---------------------------공통 (/scalp/result/v2 ,위드비컨 샴푸 추천)---------------------------
    const {
      data: { profile },
    } = yield call(quizAPI.submitQuiz, action.payload);
    // ---------------------------위드비컨 샴푸 추천 analysis Page START---------------------------
    if (action.payload.isBeconShampoo) {
      const {
        data: { products },
      } = yield call(analysisAPI.getOwnProducts);

      if (action.payload.diagnosis_items) {
        // analysis 페이지 -> 진단 item + quizData있을 경우
        const scalpType = yield call(scalpAPI.getScalpType, action.payload);

        const kit = makeKit({
          scalp_type: scalpType.data.adjust_scalps.scalp_types.primary,
          scalp_type_2: scalpType.data.adjust_scalps.scalp_types?.secondary
            ? scalpType.data.adjust_scalps.scalp_types?.secondary
            : null,
          hair_preference: profile.preference.hair_1,
          treatment_product: profile.lifestyle.treatment_product,
          products, //위드비컨 샴푸
          quizzes: action.payload.quizState,
          product_variation: 'main',
        });
        const issues = stateGoodBadCategory(
          scalpType.data.adjust_scalps.scalp_types.primary,
          scalpType.data.adjust_scalps?.cares,
          false,
        );

        // scalp reducer 참고
        yield put({
          type: GET_SCALP_TYPE_SUCCESS,
          payload: scalpType.data,
        });
        yield put({
          type: POST_QUIZ_SUCCESS,
          data: { profile, kit, issues },
        });
      } else {
        // * analysis 페이지 + 메인에서 사용중인 샴푸 클릭 후 모달 open 됐을 때 기존문진 사용하기 -> 진단 item 없고, quizData있을 경우
        const kit = makeKit({
          scalp_type: profile.scalp_type,
          scalp_type_2: profile.scalp_type_2 ? profile.scalp_type_2 : null,
          hair_preference: profile.preference.hair_1,
          treatment_product: profile.lifestyle.treatment_product,
          products, //위드비컨 샴푸
          quizzes: action.payload.quizState,
          product_variation: 'main',
        });
        const issues = stateGoodBadCategory(
          profile.scalp_type,
          profile,
          action.payload.isBeconShampoo,
        );
        yield put({
          type: POST_QUIZ_SUCCESS,
          data: { profile, kit, issues },
        });
      }
    }
    // ---------------------------위드비컨 샴푸 추천 analysis Page END---------------------------
    // ---------------------------/scalp/result/v2 START---------------------------
    else {
      const scalpType = yield call(scalpAPI.getScalpType, action.payload);
      // /scalp/result/v2 나에게 맞는 샴푸 TOP 5
      // 문진(scalp_diagnosis_items)있으면 post ,없으면 get.
      const { data } = yield call(shampooAPI.getTopRankedShampooList, {
        user_id: action.payload.user_id,
        diagnosis_items: action.payload.diagnosis_items,
      });

      const issues = stateGoodBadCategory(
        scalpType.data.adjust_scalps.scalp_types.primary,
        scalpType.data.adjust_scalps?.cares,
        action.payload.isBeconShampoo,
      );
      const {
        data: { products },
      } = yield call(analysisAPI.getOwnProducts);

      const kit = makeKit({
        scalp_type: scalpType.data.adjust_scalps.scalp_types.primary,
        scalp_type_2: scalpType.data.adjust_scalps.scalp_types.secondary
          ? scalpType.data.adjust_scalps.scalp_types.secondary
          : null,
        hair_preference: profile.preference.hair_1,
        treatment_product: profile.lifestyle.treatment_product,
        products, //위드비컨 샴푸
        quizzes: action.payload.quizState,
        product_variation: 'main',
      });

      console.log('kit', kit);

      // scalp reducer 참고
      yield put({
        type: GET_SCALP_TYPE_SUCCESS,
        payload: scalpType.data,
      });
      // shampoo reducer 참고
      // 1. TOP RANK SHAMPOO 중 랜덤하게 한개 뽑은 후, setSelectedShampooId ()-> shampoo.selectedShampooId
      // 2. dispatch -> postShampooResult() 샴푸분석,
      // 3. getShampooIngredients -> 샴푸 전성분 얻기.
      yield put({
        type: GET_TOP_RANKED_SHAMPOO_LIST_SUCCESS,
        payload: data,
      });
      yield put({
        type: POST_QUIZ_SUCCESS,
        data: { profile, kit, issues },
      });
    }
    // ---------------------------/scalp/result/v2 END---------------------------

    // user reducer 참고
    yield put({
      type: CHANGE_USER_QUIZ,
      data: profile,
    });
  } catch (error) {
    console.log(error);
    yield put({
      type: POST_QUIZ_FAILURE,
      error,
    });
  }
}
//----------------------------------------------------------------------------
// saga 생성
export function* quizSaga() {
  yield takeLatest(POST_QUIZ, sumbmitQuizSaga);
  yield takeLatest(
    RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB,
    recentQuizResultWithDiangosisKbSaga,
  );
  yield takeLatest(RECENT_QUIZ_RESULT_KB, recentQuizResultKbSaga);
}
//----------------------------------------------------------------------------
const initialState = {
  quizState: {
    ...new Array(23)
      .fill(0)
      .reduce((acc, key, i) => Object.assign(acc, { [i + 1]: {} }), {}),
  },
  quizSubmitLoading: false,
  quizSubmitDone: false,
  quizSubmitError: false,
  userQuizData: null,
  kit: null,
  issues: null,
  beconShampoo: false,
  fromLocation: '',
};
//----------------------------------------------------------------------------
export default handleActions(
  {
    //퀴즈 초기화
    [INIT_QUIZ]: () => ({
      ...initialState,
    }),
    //퀴즈 리셋
    [RESET_QUIZ]: (state, { payload: { id, quiz } }) =>
      produce(state, (draft) => {
        draft['quizState'][id][quiz] = initialState['quizState'][id][quiz];
      }),
    // 퀴즈 업데이트
    [UPDATE_QUIZ]: (state, { payload: { id, option, quiz } }) =>
      produce(state, (draft) => {
        draft['quizState'][id][quiz] = option;
      }),
    // 사용자 퀴즈 등록 요청
    [POST_QUIZ]: (state) => ({
      ...state,
      quizSubmitLoading: true,
      quizSubmitDone: false,
      quizSubmitError: false,
      userQuizData: null,
      kit: null,
      issues: null,
    }),
    // 사용자 퀴즈 등록 성공
    [POST_QUIZ_SUCCESS]: (state, { data: { profile, kit, issues } }) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitDone: true,
      userQuizData: profile,
      kit,
      issues,
    }),
    // 사용자 퀴즈 등록 실패
    [POST_QUIZ_FAILURE]: (state, payload) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitError: payload.error.response.data.code,
    }),

    /** 퀴즈 최근 결과보기 -> (퀴즈이력 ) ======== For KB  */
    [RECENT_QUIZ_RESULT_KB]: (state) => ({
      ...state,
      quizSubmitLoading: true,
      quizSubmitDone: false,
      quizSubmitError: false,
      userQuizData: null,
      kit: null,
      issues: null,
    }),

    /** 퀴즈 최근 결과보기 성공 -> (퀴즈이력 ) ======== For KB  */
    [RECENT_QUIZ_RESULT_KB_SUCCESS]: (
      state,
      { data: { profile, kit, issues } },
    ) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitDone: true,
      userQuizData: profile,
      kit,
      issues,
    }),

    /** 퀴즈 최근 결과보기 실패 -> (퀴즈이력 ) ======== For KB  */
    [RECENT_QUIZ_RESULT_KB_FAILURE]: (state, { error }) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitError: error,
    }),

    /** 퀴즈 최근 결과보기 -> (퀴즈이력 + 두피진단) ======== For KB  */
    [RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB]: (state) => ({
      ...state,
      quizSubmitLoading: true,
      quizSubmitDone: false,
      quizSubmitError: false,
      userQuizData: null,
      kit: null,
      issues: null,
    }),
    /** 퀴즈 최근 결과보기 성공 -> (퀴즈이력 + 두피진단) ======== For KB  */
    [RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_SUCCESS]: (
      state,
      { data: { profile, kit, issues } },
    ) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitDone: true,
      userQuizData: profile,
      kit,
      issues,
    }),
    /** 퀴즈 최근 결과보기 실패 -> (퀴즈이력 + 두피진단) ======== For KB  */
    [RECENT_QUIZ_RESULT_WITH_DIAGNOSIS_KB_FAILURE]: (state, { error }) => ({
      ...state,
      quizSubmitLoading: false,
      quizSubmitError: error,
    }),

    // analysis 페이지에서 이전버튼 눌렀을 경우 정상적인 페이지 이동을 위해서 quizSubmitDone 상태변경
    [CHANGE_QUIZ_SUBMIT_STATE]: (state) => ({
      ...state,
      quizSubmitDone: false,
    }),
    // /scalp/result/v2 -- 분기 진위여부 --withBeconShampoo
    [CHANGE_USER_QUIZ_STATUS]: (state, { payload: { isBeconShampoo } }) => ({
      ...state,
      beconShampoo: isBeconShampoo,
    }),
    // analysis page -> 상품 select
    [UPDATE_PICKED_SHAMPOO]: (state, { payload: { index, picked } }) =>
      produce(state, (draft) => {
        draft.kit[index].picked = picked;
      }),
    // Kit detail 페이지 -> 헤더에 있는 kit 제품 상태 update
    [UPDATE_KIT_HEADER]: (state, { payload: { index, selectedKitProduct } }) =>
      produce(state, (draft) => {
        draft.kit[index].selectedKitProduct = selectedKitProduct;
      }),
    // * quiz 마지막 페이지에서 제출하기 클릭 하였을 경우 페이지 분기를 위한 Action Type
    [SET_LOCATION_FOR_QUIZ]: (state, { payload }) => ({
      ...state,
      fromLocation: payload,
    }),
  },
  initialState,
);
